/**
 * Stage 6b: Editorial Review — the flag-only critic.
 *
 * A skeptical reviewer that cannot edit the article: it verifies claims against
 * the run's research evidence and business context, and flags unsourced stats,
 * fact drift, LLM-tell phrasing, broken anchor grammar, and diluted
 * differentiation. Output lands at document.preflight.review.
 *
 * auto_fix flags are executed by the Final Polish stage; human_review flags are
 * for you — advisory only. The run never pauses here: the flags surface again
 * as a banner on the publish panel, where you review the draft before
 * publishing (edit the article via the document editor if needed).
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import type { ArticleDocument } from '../types';

type ReviewFlag = {
  id?: string;
  category?: string;
  severity?: 'high' | 'medium' | 'low';
  action?: 'auto_fix' | 'human_review';
  quote?: string;
  location?: string;
  issue?: string;
  fix_hint?: string;
};

type ReviewReport = {
  assessment?: string;
  flags?: ReviewFlag[];
  auto_fix_count?: number;
  human_review_count?: number;
};

const SEVERITY_BADGE: Record<string, string> = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-50 text-gray-600 border-gray-200',
};

export function renderReviewOutput(doc: ArticleDocument) {
  const review = (doc.preflight as any)?.review as ReviewReport | undefined;
  if (!review) {
    return <div className="text-sm text-gray-500">Not run yet.</div>;
  }

  const flags = Array.isArray(review.flags) ? review.flags : [];
  const highHuman = flags.filter((f) => f.action === 'human_review' && f.severity === 'high');

  return (
    <div className="space-y-3 text-xs text-gray-700">
      {review.assessment && (
        <div className="rounded-lg border bg-gray-50 p-3 text-sm text-gray-800">{review.assessment}</div>
      )}

      <div className="flex gap-2">
        <span className="inline-block px-2 py-0.5 rounded border bg-gray-50">
          {flags.length} flag{flags.length === 1 ? '' : 's'}
        </span>
        <span className="inline-block px-2 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200">
          {review.auto_fix_count ?? flags.filter((f) => f.action === 'auto_fix').length} auto-fix (handled by Final Polish)
        </span>
        <span className="inline-block px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
          {review.human_review_count ?? flags.filter((f) => f.action === 'human_review').length} for human review
        </span>
      </div>

      {highHuman.length > 0 && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-red-800">
          <span className="font-medium">{highHuman.length} high-severity flag{highHuman.length === 1 ? '' : 's'} to review before publishing.</span>{' '}
          Advisory only — the build was not paused. These also appear on the publish panel; edit the article via the document editor if needed.
        </div>
      )}

      {flags.length === 0 && (
        <div className="rounded border border-green-200 bg-green-50 p-3 text-green-800">
          No editorial flags — the reviewer found nothing to raise.
        </div>
      )}

      {flags.map((f, i) => (
        <div key={f.id || i} className="border rounded p-2 space-y-1">
          <div className="flex items-center gap-2">
            <span className={`inline-block px-1.5 py-0.5 rounded border font-medium ${SEVERITY_BADGE[f.severity || 'low']}`}>
              {f.severity || 'low'}
            </span>
            <span className="font-medium">{f.category || 'other'}</span>
            <span className={`ml-auto inline-block px-1.5 py-0.5 rounded border ${
              f.action === 'auto_fix'
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}>
              {f.action === 'auto_fix' ? 'auto-fix' : 'human review'}
            </span>
          </div>
          {f.quote && <blockquote className="border-l-2 border-gray-300 pl-2 italic text-gray-600">“{f.quote}”</blockquote>}
          {f.location && <div className="text-gray-400">In: {f.location}</div>}
          {f.issue && <div>{f.issue}</div>}
          {f.fix_hint && (
            <div className="text-gray-500">
              <span className="text-gray-400">{f.action === 'auto_fix' ? 'Fix:' : 'Check:'}</span> {f.fix_hint}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function Stage6bEditorialReviewV2() {
  return (
    <StagePanel
      stageId="stage_6b_editorial_review"
      title="Stage 6b: Editorial Review"
      description="Flag-only critic: verifies every claim against the run's research evidence and business context, and flags unsourced statistics, fact drift, LLM-tell phrasing, and diluted differentiation. It never edits the article and never pauses the run — auto-fix flags go to Final Polish; factual flags surface here and on the publish panel for your review before publishing."
      isLlmStage
      renderOutput={renderReviewOutput}
    />
  );
}
